import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";

import { AppSettings } from "@/components/Apps/AppSettings";
import { Button } from "@/components/ui/button";
import { CreatorSettings } from "@/components/Creators/CreatorSettings";
import { api } from "~/convex/_generated/api";
import { createFileRoute } from "@tanstack/react-router";
import { useAccount } from "@/state/useAccount";
import { useAction } from "convex/react";
import { useDoubleCheck } from "@/components/ui/use-double-check";
import { useEffect } from "react";
import { useSignOut } from "@/utils/misc";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/settings/")(
  {
    component: DashboardSettings,
    beforeLoad: () => ({
      title: "Settings",
      headerTitle: "Settings",
      headerDescription: "Manage your account settings.",
    }),
  },
);

export default function DashboardSettings() {
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const { appId, clearAccount } = useAccount();
  const signOut = useSignOut();
  const { mutateAsync: deleteCurrentUserAccount } = useMutation({
    mutationFn: useConvexMutation(api.app.deleteCurrentUserAccount),
  });
  const { doubleCheck, getButtonProps } = useDoubleCheck();

  // Mutations for profile analysis
  const processYouTubeProfile = useAction(
    api.exp__creatorInsights.exp__processCreatorProfileYT,
  );

  const processInstagramProfile = useAction(
    api.exp__creatorInsights.exp__processCreatorProfileInstagram,
  );
  const processTikTokProfile = useAction(
    api.exp__creatorInsights.exp__processCreatorProfileTikTok,
  );

  // Function to trigger profile analysis for connected social accounts
  const triggerProfileAnalysis = async () => {
    if (!user?.creator) return;

    try {
      for (const youtube of user.creatorSocials.youtube || []) {
        await processYouTubeProfile({ handle: youtube.handle });
      }
      for (const instagram of user.creatorSocials.instagram || []) {
        await processInstagramProfile({ handle: instagram.handle });
      }
      for (const tiktok of user.creatorSocials.tiktok || []) {
        await processTikTokProfile({ handle: tiktok.handle });
      }
    } catch (error) {
      console.error("Error during profile analysis:", error);
    }
  };

  // Trigger profile analysis when user visits settings page
  useEffect(() => {
    if (user?.creator) {
      // Check if profile analysis has already been run
      const profileAnalysisKey = `profile_analysis_completed_${user._id}`;
      const hasAnalysisBeenRun = localStorage.getItem(profileAnalysisKey);

      if (!hasAnalysisBeenRun) {
        // Set flag immediately to prevent multiple runs
        localStorage.setItem(profileAnalysisKey, "true");

        // Add 1 second delay before triggering analysis
        const timer = setTimeout(() => {
          triggerProfileAnalysis();
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [user?.creator]);

  const handleDeleteAccount = async () => {
    await deleteCurrentUserAccount({});
    // clear local storage
    clearAccount();
    localStorage.clear();
    signOut();
  };

  if (!user) {
    return null;
  }

  return (
    <div className="flex h-full w-full flex-col gap-6">
      {appId ? (
        <AppSettings user={user} appId={appId} />
      ) : (
        <CreatorSettings user={user} />
      )}

      {/* Delete Account */}
      <div className="flex w-full flex-col items-start rounded-lg border border-destructive bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">Delete Account</h2>
          <p className="text-sm font-normal text-primary/60">
            Permanently delete your account, all of your projects, links and
            their respective stats.
          </p>
        </div>
        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-red-500/10 px-6 dark:bg-red-500/10">
          <p className="text-sm font-normal text-primary/60">
            This action cannot be undone, proceed with caution.
          </p>
          <Button
            size="sm"
            variant="destructive"
            {...getButtonProps({
              onClick: doubleCheck ? handleDeleteAccount : undefined,
            })}
          >
            {doubleCheck ? "Are you sure?" : "Delete Account"}
          </Button>
        </div>
      </div>
    </div>
  );
}
